import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { showSaveError, showSaveSuccess } from '@/lib/swal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import type { GrnLine } from '../types'
import { newGrnLine } from '../types'
import { useAuthStore } from '@/store/auth.store'
import {
  grnService,
  type GrnPurchaseOrderOption,
  type GrnSaveLine,
  type GrnTaxRateOption,
  type GrnVendorOption,
  type GrnWarehouseOption,
} from '@/services/grnService'
import { itemService } from '@/services/itemService'

const today = () => new Date().toISOString().slice(0, 10)

function vendorDisplayName(v: GrnVendorOption, lang: string) {
  const useAr = String(lang).toLowerCase().startsWith('ar')
  if (useAr) return v.vendorNameAr || v.vendorNameEn || String(v.id)
  return v.vendorNameEn || v.vendorNameAr || String(v.id)
}

function warehouseDisplayLabel(w: GrnWarehouseOption) {
  const c = w.code.trim()
  const n = w.name.trim()
  if (c && n) return `${c} — ${n}`
  return n || c || String(w.id)
}

function taxRateLabel(r: GrnTaxRateOption, lang: string) {
  const useAr = String(lang).toLowerCase().startsWith('ar')
  const name = useAr ? r.taxRateNameAr || r.taxRateNameEn : r.taxRateNameEn || r.taxRateNameAr
  const label = name.trim() || String(r.id)
  return `${label} (${r.percent}%)`
}

/** Amounts & save quantity: use "To receive" when set; otherwise use "Qty received" (so clearing To receive after QR does not zero totals). */
function effectiveGrnLineQty(l: GrnLine): number {
  const tr = Number(l.toReceive || 0)
  if (tr > 0) return tr
  return Number(l.quantityReceived || 0)
}

const taxableAmount = (l: GrnLine) => {
  const qty = effectiveGrnLineQty(l)
  const price = Number(l.unitPrice || 0)
  const discount = Number(l.discountPercent || 0)
  const gross = qty * price
  return Math.max(0, gross * (1 - discount / 100))
}
const taxAmount = (l: GrnLine) => taxableAmount(l) * (Number(l.taxPercent || 0) / 100)
const lineTotal = (l: GrnLine) => taxableAmount(l) + taxAmount(l)

function isoDateInput(v: unknown): string {
  const s = String(v ?? '')
  return s ? s.slice(0, 10) : ''
}

type GrnItemMeta = { itemCode: string; itemNameEn: string; itemNameAr: string }

function grnBatchCacheKey(whId: number, itemId: number, orderId: number) {
  return `${whId}:${itemId}:${orderId}`
}

function formatGrnItemLabel(meta: GrnItemMeta | undefined, lang: string, id: number) {
  if (!meta) return ''
  const useAr = lang.toLowerCase().startsWith('ar')
  const name = useAr ? meta.itemNameAr || meta.itemNameEn : meta.itemNameEn || meta.itemNameAr
  const code = meta.itemCode.trim()
  const nameTrim = name.trim()
  if (code && nameTrim) return `${code} — ${nameTrim}`
  if (nameTrim) return nameTrim
  if (code) return code
  return `Item #${id}`
}

function toGrnSaveLine(l: GrnLine, noteId: number): GrnSaveLine {
  const qty = effectiveGrnLineQty(l)
  const price = Number(l.unitPrice || 0)
  const discPct = Number(l.discountPercent || 0)
  const gross = qty * price
  const discAmt = gross * (discPct / 100)
  // New GRN (noteId === 0): line id must be 0 per API / Swagger. PO line id lives in l.id — do not send it on create.
  const lineIdForApi = noteId > 0 ? (Number(l.id) || 0) : 0
  return {
    id: lineIdForApi,
    itemId: Number(l.itemId),
    batchId: Number(l.batchId),
    quantity: qty,
    unitPrice: price,
    discount: discPct,
    discountAmount: Math.round(discAmt * 10000) / 10000,
    tax: Number(l.taxRateId) || 0,
    taxPercent: Number(l.taxPercent || 0),
    taxableAmount: Math.round(taxableAmount(l) * 10000) / 10000,
    taxAmount: Math.round(taxAmount(l) * 10000) / 10000,
    lineAmount: Math.round(lineTotal(l) * 10000) / 10000,
    noteId,
    isDeleted: 'false',
    quantityReturned: 0,
  }
}

function mapPoLineToGrnLine(raw: Record<string, unknown>, taxRates: GrnTaxRateOption[]): GrnLine {
  const qit = raw.quantityInTransit
  const useTransit =
    qit != null && qit !== '' && Number.isFinite(Number(qit)) && Number(qit) >= 0
  const orderQuantity = useTransit ? Number(qit) : Number(raw.quantity ?? 0)
  const qtyRecv = Number(raw.quantityReceived ?? 0)
  const remaining = Math.max(0, orderQuantity - qtyRecv)
  let taxRateId = raw.tax != null && raw.tax !== '' ? String(raw.tax) : ''
  const taxPercent = Number(raw.taxPercent ?? 0)
  if (!taxRateId && taxPercent > 0 && taxRates.length > 0) {
    const match = taxRates.find((r) => Math.abs(r.percent - taxPercent) < 0.001)
    if (match) taxRateId = String(match.id)
  }
  return {
    id: Number(raw.id ?? 0),
    itemId: raw.itemId != null && raw.itemId !== '' ? String(raw.itemId) : '',
    batchId: '',
    batchLabel: undefined,
    orderQuantity,
    quantityReceived: qtyRecv,
    toReceive: remaining,
    unitPrice: Number(raw.unitPrice ?? 0),
    taxRateId,
    taxPercent,
    discountPercent: Number(raw.discount ?? 0),
  }
}

function numField(raw: Record<string, unknown>, keys: string[], fallback = 0): number {
  for (const k of keys) {
    const v = raw[k]
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v)
  }
  return fallback
}

function strField(raw: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = raw[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

/** Map `GET /api/Grn/GetGrnLinesByGrnId/{id}` row → form line (preserves line `id` for update). */
function mapGrnApiLineToGrnLine(raw: Record<string, unknown>): GrnLine {
  const qty = numField(raw, ['quantity', 'Quantity', 'qty', 'Qty'])
  const qtyRecv = numField(raw, ['quantityReceived', 'QuantityReceived', 'qtyReceived'], qty)
  const orderQty = numField(raw, ['orderQuantity', 'OrderQuantity', 'orderQty'], qty)
  const taxRateId = strField(raw, ['tax', 'Tax', 'taxId', 'TaxId', 'taxRateId', 'TaxRateId'])
  const taxPercent = numField(raw, ['taxPercent', 'TaxPercent'])
  const discountPct = numField(raw, ['discount', 'Discount'])
  const batchIdStr = strField(raw, ['batchId', 'BatchId', 'batch_id'])
  const batchLbl = strField(raw, [
    'batchCode',
    'BatchCode',
    'batchName',
    'BatchName',
    'batchLabel',
    'batchReference',
    'reference',
    'Reference',
  ])
  return {
    id: numField(raw, ['id', 'Id', 'lineId', 'LineId']),
    itemId: strField(raw, ['itemId', 'ItemId', 'item_id']),
    batchId: batchIdStr,
    batchLabel: batchLbl || (batchIdStr ? `Batch #${batchIdStr}` : undefined),
    orderQuantity: orderQty > 0 ? orderQty : qty,
    quantityReceived: qtyRecv,
    toReceive: numField(raw, ['toReceive', 'ToReceive']),
    unitPrice: numField(raw, ['unitPrice', 'UnitPrice']),
    taxRateId,
    taxPercent,
    discountPercent: discountPct,
  }
}

export const GoodsReceiptNotesEdit: React.FC = () => {
  const { i18n, t } = useTranslation('common')
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)
  const userId = useAuthStore((s) => s.user?.userId)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(id && id !== 'new')

  /** Edit: GetOrders/GetWarehouses may omit the saved id — merge these after master load / list fetch */
  const editPoOptionFallbackRef = useRef<{ id: number; code: string } | null>(null)
  const editWarehouseOptionFallbackRef = useRef<{ id: number; code: string; name: string } | null>(null)
  /** Mirrors ref so PO Select + batch fetch re-render with API id even if lists load late */
  const [editForcedPoOption, setEditForcedPoOption] = useState<{ id: number; code: string } | null>(null)

  useEffect(() => {
    if (!isEdit) {
      editPoOptionFallbackRef.current = null
      editWarehouseOptionFallbackRef.current = null
      setEditForcedPoOption(null)
    }
  }, [isEdit])

  // Order Details
  const [vendorId, setVendorId] = useState('')
  const [purchaseOrderId, setPurchaseOrderId] = useState('')
  const [reference, setReference] = useState('')
  const [warehouseId, setWarehouseId] = useState('')
  const [orderDate, setOrderDate] = useState(today())

  // GRN Details
  const [grnDate, setGrnDate] = useState(today())
  const [grnReference, setGrnReference] = useState('')
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)

  const [vendors, setVendors] = useState<GrnVendorOption[]>([])
  const [vendorsLoading, setVendorsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setVendorsLoading(true)
        const list = await grnService.getAllVendors()
        if (!cancelled) setVendors(list)
      } catch {
        if (!cancelled) toast.error('Failed to load vendors.')
      } finally {
        if (!cancelled) setVendorsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const [purchaseOrders, setPurchaseOrders] = useState<GrnPurchaseOrderOption[]>([])
  const [purchaseOrdersLoading, setPurchaseOrdersLoading] = useState(false)

  useEffect(() => {
    if (!isEdit) setPurchaseOrderId('')
    if (!vendorId) {
      setPurchaseOrders([])
      setPurchaseOrdersLoading(false)
      return
    }
    const vid = Number(vendorId)
    if (!Number.isFinite(vid) || vid <= 0) {
      setPurchaseOrders([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setPurchaseOrdersLoading(true)
        const list = await grnService.getOrdersByVendor(vid)
        if (!cancelled) {
          setPurchaseOrders(() => {
            const merged = [...list]
            const fb = editPoOptionFallbackRef.current
            if (isEdit && fb && fb.id > 0 && !merged.some((p) => p.id === fb.id)) {
              merged.push({ id: fb.id, code: fb.code })
            }
            return merged
          })
        }
      } catch {
        if (!cancelled) {
          setPurchaseOrders([])
          toast.error('Failed to load purchase orders.')
        }
      } finally {
        if (!cancelled) setPurchaseOrdersLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [vendorId, isEdit])

  const [warehouses, setWarehouses] = useState<GrnWarehouseOption[]>([])
  const [warehousesLoading, setWarehousesLoading] = useState(false)

  useEffect(() => {
    if (!isEdit) setWarehouseId('')
    if (!branchId) {
      setWarehouses([])
      setWarehousesLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setWarehousesLoading(true)
        const list = await grnService.getWarehouses(branchId)
        if (!cancelled) {
          setWarehouses(() => {
            const merged = [...list]
            const fb = editWarehouseOptionFallbackRef.current
            if (isEdit && fb && fb.id > 0 && !merged.some((w) => w.id === fb.id)) {
              merged.push({ id: fb.id, code: fb.code, name: fb.name })
            }
            return merged
          })
        }
      } catch {
        if (!cancelled) {
          setWarehouses([])
          toast.error('Failed to load warehouses.')
        }
      } finally {
        if (!cancelled) setWarehousesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [branchId, isEdit])

  const [taxRates, setTaxRates] = useState<GrnTaxRateOption[]>([])
  const [taxRatesLoading, setTaxRatesLoading] = useState(true)
  const taxRatesRef = useRef<GrnTaxRateOption[]>([])
  taxRatesRef.current = taxRates

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setTaxRatesLoading(true)
        const list = await grnService.getTaxRates()
        if (!cancelled) setTaxRates(list)
      } catch {
        if (!cancelled) {
          setTaxRates([])
          toast.error('Failed to load tax rates.')
        }
      } finally {
        if (!cancelled) setTaxRatesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const [lines, setLines] = useState<GrnLine[]>(Array.from({ length: 3 }, () => newGrnLine()))
  const [grnEditLoading, setGrnEditLoading] = useState(false)

  useEffect(() => {
    if (!taxRates.length) return
    setLines((prev) => {
      const def = taxRates.find((r) => r.isDefault) ?? taxRates[0]
      if (!def) return prev
      let changed = false
      const next = prev.map((row) => {
        if (row.taxRateId) return row
        changed = true
        return { ...row, taxRateId: String(def.id), taxPercent: def.percent }
      })
      return changed ? next : prev
    })
  }, [taxRates])

  useEffect(() => {
    if (!isEdit || !id || id === 'new') return
    const nid = Number(id)
    if (!Number.isFinite(nid) || nid <= 0) return
    let cancelled = false
    ;(async () => {
      try {
        setGrnEditLoading(true)
        const master = await grnService.getGrnById(nid)
        if (cancelled) return
        const forcedPo =
          master.purchaseOrderId != null && Number(master.purchaseOrderId) > 0
            ? {
                id: Number(master.purchaseOrderId),
                code: master.purchaseorderCode?.trim() || `PO #${Number(master.purchaseOrderId)}`,
              }
            : null
        editPoOptionFallbackRef.current = forcedPo
        setEditForcedPoOption(forcedPo)
        editWarehouseOptionFallbackRef.current =
          master.warehouseId != null && Number(master.warehouseId) > 0
            ? {
                id: Number(master.warehouseId),
                code: master.warehouseCode?.trim() || String(Number(master.warehouseId)),
                name:
                  master.warehouseName?.trim() ||
                  master.warehouseCode?.trim() ||
                  `Warehouse ${Number(master.warehouseId)}`,
              }
            : null
        const apiLines = await grnService.getGrnLinesByGrnId(nid)
        if (cancelled) return
        if (master.purchaseOrderId != null && Number(master.purchaseOrderId) > 0) {
          const pid = Number(master.purchaseOrderId)
          const poCode = master.purchaseorderCode?.trim() || `PO #${pid}`
          setPurchaseOrders((prev) => (prev.some((p) => p.id === pid) ? prev : [...prev, { id: pid, code: poCode }]))
        }
        if (master.warehouseId != null && Number(master.warehouseId) > 0) {
          const wid = Number(master.warehouseId)
          const wName =
            master.warehouseName?.trim() || master.warehouseCode?.trim() || `Warehouse ${wid}`
          const wCode = master.warehouseCode?.trim() || String(wid)
          setWarehouses((prev) =>
            prev.some((w) => w.id === wid) ? prev : [...prev, { id: wid, code: wCode, name: wName }],
          )
        }
        if (master.vendorId != null && Number(master.vendorId) > 0) {
          setVendorId(String(Number(master.vendorId)))
        }
        if (master.purchaseOrderId != null && Number(master.purchaseOrderId) > 0) {
          setPurchaseOrderId(String(Number(master.purchaseOrderId)))
        }
        if (master.reference != null && String(master.reference).trim() !== '') {
          setReference(String(master.reference))
        }
        if (master.warehouseId != null && Number(master.warehouseId) > 0) {
          setWarehouseId(String(Number(master.warehouseId)))
        }
        const grnD = master.date ? isoDateInput(master.date) : ''
        if (grnD) setGrnDate(grnD)
        const ordD = master.orderDate ? isoDateInput(master.orderDate) : grnD
        if (ordD) setOrderDate(ordD)
        const docCode = [master.code, master.taxNotes].find((s) => s != null && String(s).trim() !== '')
        if (docCode != null) setGrnReference(String(docCode).trim())
        const mapped = apiLines.map((row) => mapGrnApiLineToGrnLine(row))
        setLines(mapped.length > 0 ? mapped : [newGrnLine()])
      } catch {
        if (!cancelled) {
          toast.error('Failed to load goods receipt note.')
          navigate('/inventory/goods-receipt-notes')
        }
      } finally {
        if (!cancelled) setGrnEditLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isEdit, id, navigate])

  /** If branch/warehouse or vendor/PO lists loaded before master set the fallback refs, merge again after edit load */
  useEffect(() => {
    if (!isEdit || grnEditLoading) return
    const wfb = editWarehouseOptionFallbackRef.current
    if (wfb && wfb.id > 0) {
      setWarehouses((prev) => {
        if (prev.some((w) => w.id === wfb.id)) return prev
        return [...prev, { id: wfb.id, code: wfb.code, name: wfb.name }]
      })
    }
    const pfb = editPoOptionFallbackRef.current
    if (pfb && pfb.id > 0) {
      setPurchaseOrders((prev) => {
        if (prev.some((p) => p.id === pfb.id)) return prev
        return [...prev, { id: pfb.id, code: pfb.code }]
      })
    }
  }, [isEdit, grnEditLoading])

  const effectivePurchaseOrderId = useMemo(() => {
    if (purchaseOrderId) return purchaseOrderId
    if (isEdit && editForcedPoOption && editForcedPoOption.id > 0) return String(editForcedPoOption.id)
    return ''
  }, [purchaseOrderId, isEdit, editForcedPoOption])

  /** If something clears `purchaseOrderId` on edit, restore from API-loaded PO id */
  useEffect(() => {
    if (!isEdit || !editForcedPoOption || editForcedPoOption.id <= 0) return
    if (purchaseOrderId) return
    setPurchaseOrderId(String(editForcedPoOption.id))
  }, [isEdit, editForcedPoOption, purchaseOrderId])

  const purchaseOrderSelectOptions = useMemo(() => {
    const base = [...purchaseOrders]
    const ensure = (poId: number, code: string) => {
      if (!Number.isFinite(poId) || poId <= 0) return
      if (!base.some((p) => Number(p.id) === poId)) {
        base.push({ id: poId, code: code.trim() || `PO #${poId}` })
      }
    }
    if (isEdit && editForcedPoOption) ensure(editForcedPoOption.id, editForcedPoOption.code)
    const pid = Number(purchaseOrderId)
    if (isEdit && Number.isFinite(pid) && pid > 0) ensure(pid, `PO #${pid}`)
    return base
  }, [purchaseOrders, isEdit, editForcedPoOption, purchaseOrderId])

  const lineItemIdsSignature = useMemo(
    () =>
      [...new Set(lines.map((l) => Number(l.itemId)).filter((n) => n > 0))]
        .sort((a, b) => a - b)
        .join(','),
    [lines],
  )

  const [grnBatchesByKey, setGrnBatchesByKey] = useState<Record<string, Array<{ id: number; label: string }>>>({})
  const [grnBatchesLoading, setGrnBatchesLoading] = useState(false)

  useEffect(() => {
    const wh = Number(warehouseId)
    const ord = Number(effectivePurchaseOrderId)
    if (
      !warehouseId ||
      !effectivePurchaseOrderId ||
      !Number.isFinite(wh) ||
      wh <= 0 ||
      !Number.isFinite(ord) ||
      ord <= 0
    ) {
      setGrnBatchesByKey({})
      setGrnBatchesLoading(false)
      return
    }
    const itemIds = lineItemIdsSignature
      ? lineItemIdsSignature.split(',').map((s) => Number(s)).filter((n) => n > 0)
      : []
    if (itemIds.length === 0) {
      setGrnBatchesByKey({})
      setGrnBatchesLoading(false)
      return
    }
    let cancelled = false
    setGrnBatchesLoading(true)
    setGrnBatchesByKey({})
    ;(async () => {
      const next: Record<string, Array<{ id: number; label: string }>> = {}
      try {
        await Promise.all(
          itemIds.map(async (itemId) => {
            const key = grnBatchCacheKey(wh, itemId, ord)
            try {
              next[key] = await grnService.getBatches(wh, itemId, ord)
            } catch {
              next[key] = []
            }
          }),
        )
      } finally {
        if (!cancelled) {
          setGrnBatchesByKey(next)
          setGrnBatchesLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
      setGrnBatchesLoading(false)
    }
  }, [warehouseId, effectivePurchaseOrderId, lineItemIdsSignature])

  const [itemMetaById, setItemMetaById] = useState<Record<number, GrnItemMeta>>({})
  const [itemsNameLoading, setItemsNameLoading] = useState(false)
  const itemMetaByIdRef = useRef<Record<number, GrnItemMeta>>({})
  itemMetaByIdRef.current = itemMetaById

  useEffect(() => {
    setItemMetaById({})
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setItemsNameLoading(false)
      return
    }
    const bid = Number(branchId)
    const lineIds = [...new Set(lines.map((l) => Number(l.itemId)).filter((n) => n > 0))]
    if (lineIds.length === 0) {
      setItemsNameLoading(false)
      return
    }
    const missing = lineIds.filter((id) => !itemMetaByIdRef.current[id])
    if (missing.length === 0) {
      setItemsNameLoading(false)
      return
    }
    let cancelled = false
    setItemsNameLoading(true)
    ;(async () => {
      const updates: Record<number, GrnItemMeta> = {}
      await Promise.all(
        missing.map(async (id) => {
          try {
            const it = await itemService.getItemById(id, bid)
            updates[id] = {
              itemCode: it.itemCode || '',
              itemNameEn: it.itemNameEn || '',
              itemNameAr: it.itemNameAr || '',
            }
          } catch {
            updates[id] = { itemCode: '', itemNameEn: '', itemNameAr: '' }
          }
        }),
      )
      if (cancelled) return
      setItemMetaById((p) => ({ ...p, ...updates }))
      setItemsNameLoading(false)
    })()
    return () => {
      cancelled = true
      setItemsNameLoading(false)
    }
  }, [lines, branchId])

  const prevPurchaseOrderIdRef = useRef<string | undefined>(undefined)
  const [orderLinesLoading, setOrderLinesLoading] = useState(false)

  useEffect(() => {
    if (!purchaseOrderId) {
      const hadPo = prevPurchaseOrderIdRef.current
      prevPurchaseOrderIdRef.current = undefined
      // Do not wipe GRN lines on edit when PO is briefly empty (avoids clearing GetGrnLines data).
      if (hadPo && !isEdit) {
        const def = taxRates.find((r) => r.isDefault) ?? taxRates[0]
        setLines(
          Array.from({ length: 3 }, () => {
            const line = newGrnLine()
            return def ? { ...line, taxRateId: String(def.id), taxPercent: def.percent } : line
          }),
        )
      }
      return
    }
    prevPurchaseOrderIdRef.current = purchaseOrderId
  }, [purchaseOrderId, taxRates, isEdit])

  useEffect(() => {
    if (isEdit) {
      setOrderLinesLoading(false)
      return
    }
    if (!purchaseOrderId) {
      setOrderLinesLoading(false)
      return
    }
    const oid = Number(purchaseOrderId)
    if (!Number.isFinite(oid) || oid <= 0) return
    let cancelled = false
    ;(async () => {
      try {
        setOrderLinesLoading(true)
        const { order, lines: apiLines } = await grnService.getOrderLines(oid)
        if (cancelled) return
        const wid = order.warehouseId
        if (wid != null && wid !== '') setWarehouseId(String(Number(wid)))
        const refVal = order.reference
        if (refVal != null && refVal !== '') setReference(String(refVal))
        const od = order.orderDate ?? order.date
        const odStr = isoDateInput(od)
        if (odStr) setOrderDate(odStr)
        const vid = order.vendorId
        if (vid != null && vid !== '') setVendorId(String(Number(vid)))

        const mapped = apiLines.map((row) => mapPoLineToGrnLine(row, taxRatesRef.current))
        setLines(mapped.length > 0 ? mapped : [newGrnLine()])
      } catch {
        if (!cancelled) toast.error('Failed to load purchase order lines.')
      } finally {
        if (!cancelled) setOrderLinesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [purchaseOrderId, isEdit])

  const addLines = (n: number) =>
    setLines((prev) => {
      const def = taxRates.find((r) => r.isDefault) ?? taxRates[0]
      const extra = Array.from({ length: n }, () => {
        const line = newGrnLine()
        if (def) return { ...line, taxRateId: String(def.id), taxPercent: def.percent }
        return line
      })
      return [...prev, ...extra]
    })

  const updateLine = (index: number, patch: Partial<GrnLine>) => {
    setLines((prev) => {
      const next = [...prev]
      const row = next[index]
      if (!row) return prev
      const merged = { ...row, ...patch }
      // When user edits only "Qty received", clear "To receive"; totals still use QR via effectiveGrnLineQty.
      if ('quantityReceived' in patch && !('toReceive' in patch)) {
        merged.toReceive = 0
      }
      if ('toReceive' in patch || 'orderQuantity' in patch) {
        const remaining = Math.max(0, Number(merged.orderQuantity || 0) - Number(merged.quantityReceived || 0))
        merged.toReceive = Math.min(Number(merged.toReceive || 0), remaining)
      }
      next[index] = merged
      return next
    })
  }

  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index))

  const totals = useMemo(() => {
    const subTotal = lines.reduce((sum, l) => sum + taxableAmount(l), 0)
    const totalTax = lines.reduce((sum, l) => sum + taxAmount(l), 0)
    const net = subTotal + totalTax
    return { subTotal, totalTax, net }
  }, [lines])

  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const noteId =
      isEdit && id && id !== 'new' && Number.isFinite(Number(id)) && Number(id) > 0 ? Number(id) : 0

    const itemList = lines.map((l) => toGrnSaveLine(l, noteId))

    const po = purchaseOrderSelectOptions.find((p) => String(p.id) === effectivePurchaseOrderId)
    const vendor = vendors.find((v) => String(v.id) === vendorId)
    const wh = warehouses.find((w) => String(w.id) === warehouseId)
    const headerDiscount = itemList.reduce((s, l) => s + l.discountAmount, 0)
    const saveSubTotal = itemList.reduce((s, l) => s + l.taxableAmount, 0)
    const saveTax = itemList.reduce((s, l) => s + l.taxAmount, 0)
    const saveNet = itemList.reduce((s, l) => s + l.lineAmount, 0)
    const now = new Date().toISOString()
    const bid = Number(branchId) || 0
    const uid = Number(userId || 0)

    const payload = {
        id: noteId,
        code: (grnReference.trim() || reference.trim()),
        purchaseorderCode: po?.code?.trim() ?? '',
        wharehouseName: wh ? [wh.code, wh.name].filter(Boolean).join(' ').trim() : '',
        vendorNameEn: vendor
          ? (vendor.vendorNameEn?.trim() || vendorDisplayName(vendor, i18n.language))
          : '',
        reference: reference.trim(),
        purchaseOrderId: Number(effectivePurchaseOrderId) || 0,
        date: grnDate,
        vendorId: Number(vendorId) || 0,
        warehouseId: Number(warehouseId) || 0,
        grnnumber: '',
        taxNotes: grnReference.trim(),
        attachmentPath: '',
        apaccountId: 0,
        taxableAmount: saveSubTotal,
        taxAmount: saveTax,
        discount: headerDiscount,
        totalAmount: saveNet,
        payableBalance: saveNet,
        isReturnCreated: false,
        isBillCreated: false,
        status: 0,
        createdDate: now,
        createdBy: uid,
        modifiedDate: now,
        modifiedBy: uid,
        branchId: bid,
        attachmentFile: attachmentFile,
        itemList,
      }

    const payloadForConsole = {
      ...payload,
      attachmentFile: attachmentFile
        ? { name: attachmentFile.name, size: attachmentFile.size, type: attachmentFile.type }
        : null,
    }
    const savePath = noteId > 0 ? '/api/Grn/Update' : '/api/Grn/Save'
    console.log(
      '%c[GRN] Form submit%c → will call POST ' + savePath,
      'font-weight:bold;color:#fff;background:#15803d;padding:2px 6px;border-radius:4px',
      'color:inherit',
    )
    console.log('[GRN] Payload (object passed to grnService; file shown as meta only):', payloadForConsole)
    console.log('[GRN] itemList:', itemList)

    try {
      setSaving(true)
      if (noteId > 0) {
        await grnService.update(payload)
      } else {
        await grnService.save(payload)
      }
      await showSaveSuccess(isEdit)
      navigate('/inventory/goods-receipt-notes')
    } catch (err) {
      await showSaveError(err, isEdit)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Goods Receipt Notes</h1>
            <p className="text-muted-foreground text-sm">
              {grnEditLoading
                ? 'Loading goods receipt note…'
                : isEdit
                  ? `Edit — ID ${id}`
                  : 'Goods Receipt Note Details'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">Order Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Vendor</Label>
              <Select value={vendorId} onValueChange={setVendorId} disabled={vendorsLoading}>
                <SelectTrigger>
                  <SelectValue placeholder={vendorsLoading ? 'Loading vendors…' : 'Select Vendor'} />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={String(v.id)}>
                      {vendorDisplayName(v, i18n.language)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Purchase Order</Label>
              <Select
                value={effectivePurchaseOrderId}
                onValueChange={setPurchaseOrderId}
                disabled={
                  !vendorId ||
                  orderLinesLoading ||
                  (purchaseOrdersLoading && purchaseOrderSelectOptions.length === 0)
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !vendorId
                        ? 'Select vendor first'
                        : purchaseOrdersLoading && purchaseOrderSelectOptions.length === 0
                          ? 'Loading orders…'
                          : orderLinesLoading
                            ? 'Loading order…'
                            : purchaseOrderSelectOptions.length === 0
                              ? 'No orders for this vendor'
                              : 'Select Order'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {purchaseOrderSelectOptions.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.code.trim() || String(p.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Reference</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Warehouse</Label>
              <Select
                value={warehouseId}
                onValueChange={setWarehouseId}
                disabled={!branchId || warehousesLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !branchId
                        ? 'Select branch first'
                        : warehousesLoading
                          ? 'Loading warehouses…'
                          : 'Select warehouse'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={String(w.id)}>
                      {warehouseDisplayLabel(w)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Order Date</Label>
              <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">GRN Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={grnDate} onChange={(e) => setGrnDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Reference</Label>
              <Input value={grnReference} onChange={(e) => setGrnReference(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <Input type="file" onChange={(e) => setAttachmentFile(e.target.files?.[0] ?? null)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-primary/5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-lg">Order Items</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)} disabled={orderLinesLoading}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add Row
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)} disabled={orderLinesLoading}>
                  Add 3 Rows
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)} disabled={orderLinesLoading}>
                  Add 5 Rows
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px]">
                <thead>
                  <tr className="border-b bg-primary/5">
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Item</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Batch</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                      Order qty
                    </th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                      Qty received
                    </th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                      To receive
                    </th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                      Unit price
                    </th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                      Taxable amt
                    </th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Tax</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                      Tax amt
                    </th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Total</th>
                    <th className="w-14 p-3 align-middle" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const remaining = Math.max(0, Number(l.orderQuantity || 0) - Number(l.quantityReceived || 0))
                    const rowBusy = orderLinesLoading || grnEditLoading
                    const rowKey = l.id > 0 ? `po-line-${l.id}` : `row-${idx}`
                    const itemNum = Number(l.itemId)
                    const itemMeta = itemNum > 0 ? itemMetaById[itemNum] : undefined
                    const itemReadonlyText =
                      itemNum <= 0
                        ? '—'
                        : itemMeta
                          ? formatGrnItemLabel(itemMeta, i18n.language, itemNum)
                          : itemsNameLoading
                            ? 'Loading…'
                            : '…'
                    const whNum = Number(warehouseId)
                    const ordNum = Number(effectivePurchaseOrderId)
                    const batchKey =
                      itemNum > 0 &&
                      warehouseId &&
                      effectivePurchaseOrderId &&
                      Number.isFinite(whNum) &&
                      whNum > 0 &&
                      Number.isFinite(ordNum) &&
                      ordNum > 0
                        ? grnBatchCacheKey(whNum, itemNum, ordNum)
                        : ''
                    const rowBatchOptions = batchKey ? grnBatchesByKey[batchKey] ?? [] : []
                    const apiBatchNum = Number(l.batchId)
                    const batchOpts =
                      isEdit &&
                      Number.isFinite(apiBatchNum) &&
                      apiBatchNum > 0 &&
                      !rowBatchOptions.some((b) => b.id === apiBatchNum)
                        ? [
                            ...rowBatchOptions,
                            {
                              id: apiBatchNum,
                              label: l.batchLabel?.trim() || `Batch #${apiBatchNum}`,
                            },
                          ]
                        : rowBatchOptions
                    const batchPlaceholder = (() => {
                      if (!warehouseId) return 'Select warehouse'
                      if (!effectivePurchaseOrderId) return 'Select order'
                      if (itemNum <= 0) return '—'
                      if (grnBatchesLoading) return 'Loading batches…'
                      if (batchOpts.length === 0) return 'No batches'
                      return 'Select batch'
                    })()
                    return (
                      <tr
                        key={rowKey}
                        className="border-b transition-colors hover:bg-muted/40 dark:hover:bg-muted/20"
                      >
                        <td className="p-3 align-middle">
                          <div className="flex min-h-10 items-center">
                            <Input
                              readOnly
                              tabIndex={-1}
                              className="min-w-[200px] cursor-default bg-muted"
                              value={itemReadonlyText}
                              title={
                                itemNum > 0 && itemMeta
                                  ? formatGrnItemLabel(itemMeta, i18n.language, itemNum)
                                  : undefined
                              }
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle">
                          <div className="flex min-h-10 min-w-[140px] items-center">
                            <Select
                              value={l.batchId ? String(l.batchId) : ''}
                              onValueChange={(v) => {
                                const picked = batchOpts.find((b) => String(b.id) === v)
                                updateLine(idx, {
                                  batchId: v,
                                  batchLabel: picked?.label ?? l.batchLabel,
                                })
                              }}
                              disabled={
                                rowBusy ||
                                grnBatchesLoading ||
                                !warehouseId ||
                                !effectivePurchaseOrderId ||
                                itemNum <= 0
                              }
                            >
                              <SelectTrigger className="h-10 w-full">
                                <SelectValue placeholder={batchPlaceholder} />
                              </SelectTrigger>
                              <SelectContent>
                                {batchOpts.map((b) => (
                                  <SelectItem key={b.id} value={String(b.id)}>
                                    {b.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                        <td className="p-3 align-middle">
                          <div className="flex min-h-10 items-center">
                            <Input
                              type="number"
                              min={0}
                              className="h-10 w-24"
                              value={l.orderQuantity || ''}
                              onChange={(e) => updateLine(idx, { orderQuantity: Number(e.target.value) || 0 })}
                              disabled={rowBusy}
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle">
                          <div className="flex min-h-10 items-center">
                            <Input
                              type="number"
                              min={0}
                              className="h-10 w-24"
                              value={l.quantityReceived || ''}
                              onChange={(e) => updateLine(idx, { quantityReceived: Number(e.target.value) || 0 })}
                              disabled={rowBusy}
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle">
                          <div className="flex min-h-10 items-center">
                            <Input
                              type="number"
                              min={0}
                              max={remaining}
                              className="h-10 w-24"
                              value={l.toReceive || ''}
                              onChange={(e) => updateLine(idx, { toReceive: Number(e.target.value) || 0 })}
                              disabled={rowBusy}
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle">
                          <div className="flex min-h-10 items-center">
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              className="h-10 w-28"
                              value={l.unitPrice || ''}
                              onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) || 0 })}
                              disabled={rowBusy}
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle">
                          <Input
                            readOnly
                            className="h-10 w-28 bg-muted"
                            value={taxableAmount(l).toFixed(2)}
                          />
                        </td>
                        <td className="p-3 align-middle">
                          <div className="flex min-h-10 min-w-[140px] items-center">
                            <Select
                              value={l.taxRateId}
                              onValueChange={(v) => {
                                const rate = taxRates.find((r) => String(r.id) === v)
                                updateLine(idx, {
                                  taxRateId: v,
                                  taxPercent: rate?.percent ?? 0,
                                })
                              }}
                              disabled={rowBusy || taxRatesLoading || taxRates.length === 0}
                            >
                              <SelectTrigger className="h-10 w-full">
                                <SelectValue
                                  placeholder={
                                    taxRatesLoading
                                      ? 'Loading…'
                                      : taxRates.length === 0
                                        ? 'No tax rates'
                                        : 'Select tax'
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {taxRates.map((r) => (
                                  <SelectItem key={r.id} value={String(r.id)}>
                                    {taxRateLabel(r, i18n.language)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                        <td className="p-3 align-middle">
                          <Input readOnly className="h-10 w-28 bg-muted" value={taxAmount(l).toFixed(2)} />
                        </td>
                        <td className="p-3 align-middle">
                          <Input readOnly className="h-10 w-28 bg-muted" value={lineTotal(l).toFixed(2)} />
                        </td>
                        <td className="p-3 align-middle">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => removeLine(idx)}
                            disabled={rowBusy}
                            title="Delete row"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-6 rounded-md bg-primary/5 p-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <Label className="text-sm text-muted-foreground">SUB TOTAL</Label>
                  <p className="text-lg font-semibold">{totals.subTotal.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">TOTAL TAX</Label>
                  <p className="text-lg font-semibold">{totals.totalTax.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">NET AMOUNT</Label>
                  <p className="text-lg font-bold">{totals.net.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate('/inventory/goods-receipt-notes')}>
            Back
          </Button>
          <Button type="submit" disabled={saving || grnEditLoading}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? (isEdit ? 'Updating…' : 'Saving…') : isEdit ? t('common.update') : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  )
}

